import React, { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle, ArrowLeft, CheckCircle2, ChevronRight, LogIn, RotateCcw, StickyNote, X,
} from 'lucide-react';
import { useDeviceVault } from '../../contexts/DeviceVaultContext';
import { useAssessmentForm } from '../../hooks/useAssessmentForm';
import {
  fieldDomId, fieldLabel, isBlank, isVisible, missingRequired, sectionDomId,
} from '../../services/assessmentSchema';
import FormField, { NO_AUTOFILL } from './FormField';
import { MemoPanel, PreviewButton } from './MemoTools';
import { SubmitPanel, SubmittedPanel } from './SubmitTools';
import { useOnline } from './AssessmentsPage';
import { assessmentApi } from '../../services/assessmentApi';
import SaveStatus from './SaveStatus';
import SurveyPanel from './SurveyPanel';
import {
  MobileSectionBar, SectionSidebar, jumpToSection, scrollToElement, useActiveSection,
} from './SectionNav';
import { FORM_TYPE_LABEL, StatusBadge, formatDate } from './statusMeta';

function jumpToField(key) {
  const el = document.getElementById(fieldDomId(key));
  if (!el) return;
  scrollToElement(el, { align: 'center' });
  if (typeof el.focus === 'function') setTimeout(() => el.focus({ preventScroll: true }), 300);
}

function SectionNote({ section, value, onChange, disabled }) {
  const key = `note.${section.id}`;
  const [open, setOpen] = useState(!isBlank(value));
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} disabled={disabled}
        className="inline-flex items-center gap-1.5 text-sm text-blue-700 hover:text-blue-800 disabled:opacity-50">
        <StickyNote className="h-4 w-4" /> Add a note to this section
      </button>
    );
  }
  return (
    <div className="rounded-lg bg-amber-50/60 p-3 ring-1 ring-amber-100">
      <FormField
        field={{ key, label: 'Section notes', type: 'textarea' }}
        value={value}
        onChange={onChange}
        disabled={disabled}
        visible
      />
    </div>
  );
}

function SectionCard({ section, values, onChange, disabled, missing, memo }) {
  const isMemo = section.kind === 'memo' && memo;
  const showFields = !isMemo || memo.mode === 'type';
  return (
    <section
      id={sectionDomId(section.id)}
      data-section-id={section.id}
      className="scroll-mt-20 rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:p-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h2 className="text-base font-semibold text-slate-900">{section.title}</h2>
        {isMemo && memo.mode === 'type' && (
          <PreviewButton label="Preview memo PDF" flush={memo.flush} online={memo.online}
            fetchPdf={() => assessmentApi.memoPreview(memo.uuid)} filename="Memo_preview.pdf" />
        )}
      </div>
      {section.help && <p className="mt-1 text-sm text-slate-500">{section.help}</p>}
      {isMemo && (
        <MemoPanel uuid={memo.uuid} upload={memo.upload} online={memo.online} disabled={disabled}
          onServer={memo.onServer} flush={memo.flush} onDetail={memo.onDetail} mode={memo.mode} onMode={memo.onMode} />
      )}
      <div className={`mt-4 space-y-5 ${showFields ? '' : 'hidden'}`}>
        {section.fields.map((f) => (
          <FormField
            key={f.key}
            field={f}
            value={values[f.key]}
            onChange={onChange}
            disabled={disabled}
            visible={isVisible(f, values)}
            isMissing={missing.has(f.key)}
          />
        ))}
      </div>
      <div className="mt-5 border-t border-slate-100 pt-4">
        <SectionNote section={section} value={values[`note.${section.id}`]} onChange={onChange} disabled={disabled} />
      </div>
    </section>
  );
}

// A free-form scratchpad reachable from anywhere in the form, for the moments
// the conversation runs ahead of the form.
function ScratchpadPanel({ open, onClose, value, onChange, disabled }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex" role="dialog" aria-modal="true" aria-label="Session notes">
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} />
      <div className="relative ml-auto flex h-full w-full max-w-md flex-col bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-slate-900">Session notes</p>
            <p className="text-xs text-slate-500">Saved with this form. Not printed on the memo.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 text-slate-500 hover:bg-slate-100" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <textarea
            {...NO_AUTOFILL}
            autoFocus
            className="h-full min-h-[50vh] w-full resize-none rounded-lg border border-slate-300 p-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Anything worth remembering from the session…"
            value={value ?? ''}
            disabled={disabled}
            onChange={(e) => onChange('scratchpad', e.target.value === '' ? null : e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}

function Banner({ tone, icon: Icon, children, onDismiss }) {
  const tones = {
    amber: 'bg-amber-50 text-amber-900 ring-amber-200',
    red: 'bg-red-50 text-red-900 ring-red-200',
    slate: 'bg-slate-100 text-slate-800 ring-slate-200',
  };
  return (
    <div className={`flex items-start gap-2 rounded-lg p-3 text-sm ring-1 ${tones[tone]}`}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0 flex-1">{children}</div>
      {onDismiss && (
        <button type="button" onClick={onDismiss} className="rounded p-0.5 hover:bg-black/5" aria-label="Dismiss">
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

function CompletionPanel({ schema, values, status, locked, queued, hasUpload, onComplete, onReopen, onJump, submitted }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const missingForm = missingRequired(schema, values, 'form');
  const missingMemo = schema.requiresMemo && !hasUpload ? missingRequired(schema, values, 'memo') : [];

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err.body?.missing ? 'Some required answers are still missing.' : err.message);
    } finally {
      setBusy(false);
    }
  };

  if (status === 'submitted' && submitted) return submitted;

  if (locked) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600 md:p-6">
        This assessment is {status === 'submitted' ? 'submitted' : 'cancelled'} and can no longer be edited.
      </div>
    );
  }

  const completed = queued || status === 'waiting_on_memo' || status === 'ready_to_submit';
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:p-6">
      {!completed ? (
        <>
          <h2 className="text-base font-semibold text-slate-900">Finish the form</h2>
          {missingForm.length ? (
            <div className="mt-2">
              <p className="text-sm text-slate-600">Still required before the form can be marked complete:</p>
              <ul className="mt-2 space-y-1">
                {missingForm.map((key) => {
                  const info = fieldLabel(schema, key);
                  return (
                    <li key={key}>
                      <button type="button" onClick={() => onJump(key)}
                        className="inline-flex items-center gap-1 text-left text-sm text-blue-700 hover:underline">
                        <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                        {info.label} <span className="text-slate-400">· {info.sectionTitle}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <p className="mt-1 text-sm text-slate-600">
              All required answers are in. Optional questions can still be filled in after marking complete.
            </p>
          )}
          <button type="button" disabled={busy || missingForm.length > 0} onClick={() => run(onComplete)}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
            <CheckCircle2 className="h-4 w-4" /> {busy ? 'Saving…' : 'Mark form complete'}
          </button>
        </>
      ) : (
        <>
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" /> Form complete
          </h2>
          {queued && (
            <p className="mt-1 text-sm text-amber-800">
              Marked complete on this device. It will be confirmed when you are back online.
            </p>
          )}
          {!queued && status === 'waiting_on_memo' ? (
            <p className="mt-1 text-sm text-slate-600">
              The student memo still needs: {missingMemo.map((k) => fieldLabel(schema, k).label).join(', ')}.{' '}
              <button type="button" className="text-blue-700 hover:underline" onClick={() => jumpToSection('memo')}>
                Go to memo
              </button>
            </p>
          ) : !queued && (
            <p className="mt-1 text-sm text-slate-600">Ready to submit for payment.</p>
          )}
          <div className="mt-4 flex flex-wrap items-start gap-3">
            {status === 'ready_to_submit' && !queued && submitted}
            <button type="button" disabled={busy} onClick={() => run(onReopen)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              <RotateCcw className="h-4 w-4" /> Reopen form
            </button>
          </div>
        </>
      )}
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
    </div>
  );
}

export default function AssessmentFormPage() {
  const { uuid } = useParams();
  const { identity: user } = useDeviceVault();
  const form = useAssessmentForm(uuid, user?.userId);
  const { record, schema, values, sync, local, pendingCount, locked } = form;
  const [notesOpen, setNotesOpen] = useState(false);
  const online = useOnline();
  const [memoMode, setMemoMode] = useState(null);
  const [showMissing, setShowMissing] = useState(false);

  const sections = useMemo(() => schema?.sections || [], [schema]);
  const [active, setActive] = useActiveSection(sections);
  const pick = useCallback((id) => { setActive(id); jumpToSection(id); }, [setActive]);

  const missing = useMemo(() => {
    if (!showMissing || !schema) return new Set();
    return new Set([...missingRequired(schema, values, 'form'), ...missingRequired(schema, values, 'memo')]);
  }, [showMissing, schema, values]);

  const onJump = useCallback((key) => { setShowMissing(true); jumpToField(key); }, []);

  if (form.loadError) {
    return (
      <div className="mx-auto max-w-xl">
        <Banner tone="red" icon={AlertTriangle}>
          {form.loadError.status === 404
            ? 'This assessment was not found, or it is not assigned to you.'
            : `Could not open this assessment: ${form.loadError.message}`}
        </Banner>
        <Link to="/dashboard/assessments" className="mt-4 inline-flex items-center gap-1 text-sm text-blue-700">
          <ArrowLeft className="h-4 w-4" /> Back to assessments
        </Link>
      </div>
    );
  }

  if (!record || !schema) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  const status = record.server?.status || 'local';
  const upload = record.server?.memoUpload || null;
  const privileged = user?.role === 'admin' || user?.role === 'manager';
  const memo = schema.requiresMemo ? {
    uuid,
    upload,
    online,
    onServer: !!record.server,
    flush: form.flush,
    onDetail: form.applyServerDetail,
    mode: memoMode || (upload ? 'upload' : 'type'),
    onMode: setMemoMode,
  } : null;
  const date = values['meta.assessmentDate'];
  const disabled = locked;

  return (
    <div className="pb-24 lg:pb-8">
      {/* Header */}
      <div className="mb-4">
        <Link to="/dashboard/assessments" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
          <ArrowLeft className="h-4 w-4" /> Assessments
        </Link>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-slate-900 md:text-2xl">{record.studentName}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-600">
              <span>{FORM_TYPE_LABEL[record.formType]}</span>
              {date && <span>· {formatDate(date)}</span>}
              {record.therapistName && <span>· {record.therapistName}</span>}
              <StatusBadge status={status} />
            </div>
          </div>
          <div className="flex flex-col items-start gap-2 sm:items-end">
            <SaveStatus local={local} sync={sync} pendingCount={pendingCount} />
            <PreviewButton label="Preview form PDF" flush={form.flush} online={online && !!record.server}
              fetchPdf={() => assessmentApi.formPreview(uuid)} filename="Form_preview.pdf" />
          </div>
        </div>
      </div>

      {/* Banners */}
      <div className="mb-4 space-y-2">
        {local.available === false && (
          <Banner tone="red" icon={AlertTriangle}>
            This browser is not letting the portal save on this device, so answers are only safe once they
            sync. Stay online, or switch to a regular (non-private) browser window.
          </Banner>
        )}
        {sync.state === 'auth' && (
          <Banner tone="amber" icon={LogIn}>
            Your sign-in has expired. Your answers are saved on this device;{' '}
            <a href="/" target="_blank" rel="noreferrer" className="font-medium underline">sign in again in a new tab</a>
            {' '}and they will sync automatically.
          </Banner>
        )}
        {sync.state === 'denied' && (
          <Banner tone="red" icon={AlertTriangle}>
            The server refused this form ({sync.message}). Your answers are still saved on this device.
          </Banner>
        )}
        {sync.message && sync.state === 'synced' && (
          <Banner tone="amber" icon={AlertTriangle}>{sync.message}</Banner>
        )}
        {record.completeError && (
          <Banner tone="amber" icon={AlertTriangle} onDismiss={form.dismissCompleteError}>
            {record.completeError}
          </Banner>
        )}
        {record.lockedNotice && (
          <Banner tone="slate" icon={AlertTriangle} onDismiss={form.dismissLockedNotice}>
            {record.lockedNotice.count} edit(s) from this device reached the server after the form was
            {' '}{status === 'cancelled' ? 'cancelled' : 'submitted'}. They were kept on the server for an admin to review.
          </Banner>
        )}
      </div>

      <div className="lg:grid lg:grid-cols-[15rem_minmax(0,1fr)] lg:gap-6">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block">
          <div className="sticky top-0 space-y-3 pt-1">
            <SectionSidebar sections={sections} values={values} active={active} onPick={pick} memoUploaded={!!upload} />
            <button type="button" onClick={() => setNotesOpen(true)}
              className="flex w-full items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
              <StickyNote className="h-4 w-4 text-amber-600" />
              Session notes
              {!isBlank(values.scratchpad) && <span className="ml-auto h-2 w-2 rounded-full bg-amber-500" />}
            </button>
            <SurveyPanel uuid={uuid} onServer={!!record.server} cached={record.survey} onStatus={form.saveSurvey}
              followUp={record.server?.survey} />
          </div>
        </aside>

        <div className="min-w-0 space-y-4">
          {/* Mobile section bar */}
          <div className="sticky top-0 z-10 -mx-1 px-1 pt-1 lg:hidden">
            <MobileSectionBar sections={sections} values={values} active={active} onPick={pick} memoUploaded={!!upload} />
          </div>
          <div className="lg:hidden">
            <SurveyPanel uuid={uuid} onServer={!!record.server} cached={record.survey} onStatus={form.saveSurvey}
              followUp={record.server?.survey} />
          </div>

          {sections.map((s) => (
            <SectionCard key={s.id} section={s} values={values} onChange={form.setValue} memo={memo}
              disabled={disabled} missing={missing} />
          ))}

          <CompletionPanel
            schema={schema}
            values={values}
            status={status}
            locked={locked}
            queued={!!record.completeRequested}
            hasUpload={!!upload}
            submitted={status === 'submitted'
              ? <SubmittedPanel record={record} privileged={privileged} online={online} onDetail={form.applyServerDetail} />
              : <SubmitPanel record={record} values={values} online={online} survey={record.survey} onSubmit={form.submit} />}
            onComplete={form.complete}
            onReopen={form.reopen}
            onJump={onJump}
          />
        </div>
      </div>

      {/* Mobile notes button */}
      <button type="button" onClick={() => setNotesOpen(true)}
        className="fixed bottom-5 right-5 z-30 inline-flex items-center gap-2 rounded-full bg-amber-500 px-4 py-3 text-sm font-medium text-white shadow-lg hover:bg-amber-600 lg:hidden"
        aria-label="Open session notes">
        <StickyNote className="h-5 w-5" /> Notes
      </button>

      <ScratchpadPanel open={notesOpen} onClose={() => setNotesOpen(false)} value={values.scratchpad}
        onChange={form.setValue} disabled={disabled} />
    </div>
  );
}
